import classNames from 'classnames';
import Link from 'next/link';
import React, { useState } from 'react';
import { useTranslation } from '../../../utils/i18n';

interface RoomTypes {
  clinical: Array<string>;
  nonClinical: Array<string>;
}

interface Hyperlink {
  url: string;
  name: string;
}

interface Props {
  question: string;
  answer: string;
  roomTypes?: RoomTypes;
  introText?: string;
  endText?: string;
  listItems?: Array<string>;
  formattedText?: boolean;
  hyperlinks?: Array<Hyperlink>;
  hyperlinksIntroText: string;
}

const FrequentlyAskedQuestionsItems: React.FC<Props> = ({
  question,
  answer,
  roomTypes,
  introText,
  endText,
  listItems,
  formattedText,
  hyperlinks,
  hyperlinksIntroText,
}) => {
  const [currentAnswerIsShow, toggleAnswer] = useState(false);
  const { t } = useTranslation('common');

  const buttonClasses = classNames({
    button: true,
    'button--text': true,
    'button--close': currentAnswerIsShow,
    'button--open': !currentAnswerIsShow,
  });

  const renderAnswerToggle = () => {
    return (
      <button
        data-testid="toggleAnswer"
        className={buttonClasses}
        onClick={() =>
          toggleAnswer(currentAnswerIsShow => {
            return !currentAnswerIsShow;
          })
        }
        key={question}
      >
        {question}
      </button>
    );
  };

  const textWithHyperlink = (url: string, name: string) => {
    return (
      <Link href={url} target="_blank" rel="noopener noreferrer">
        {name}
        &nbsp;
      </Link>
    );
  };

  const renderRoomTypes = () => {
    return (
      roomTypes && (
        <>
          <h4>{t('shared.clinical')}</h4>
          <ul>
            {roomTypes.clinical.map(clinical => {
              return <li key={clinical}>{clinical}</li>;
            })}
          </ul>
          <br />
          <h4>{t('shared.non_clinical')}</h4>
          <ul>
            {roomTypes.nonClinical.map(nonClinical => {
              return <li key={nonClinical}>{nonClinical}</li>;
            })}
          </ul>
        </>
      )
    );
  };

  const renderAnswer = () => {
    return (
      currentAnswerIsShow &&
      answer && (
        <>
          <p key={answer}>{answer}</p>
          {renderRoomTypes()}
        </>
      )
    );
  };

  const renderFormattedAnswer = () => {
    return (
      currentAnswerIsShow &&
      formattedText && (
        <>
          <p key={introText}>{introText}</p>
          <ul className="faq__formatted-list">
            {listItems?.map(item => {
              return <li key={item}>{item}</li>;
            })}
          </ul>
          {hyperlinksIntroText && (
            <p className="faq__inline">{hyperlinksIntroText}</p>
          )}
          {roomTypes && renderRoomTypes()}
          {hyperlinks && (
            <>
              &nbsp;
              {textWithHyperlink(hyperlinks[0].url, hyperlinks[0].name)}
              {textWithHyperlink(hyperlinks[1].url, hyperlinks[1].name)}
              <p className="faq__inline">{t('shared.and')}</p>
              &nbsp;
              {textWithHyperlink(hyperlinks[2].url, hyperlinks[2].name)}
            </>
          )}
          {endText && <p>{endText}</p>}
        </>
      )
    );
  };

  return (
    <>
      {renderAnswerToggle()}
      {formattedText ? renderFormattedAnswer() : renderAnswer()}
    </>
  );
};

export default FrequentlyAskedQuestionsItems;
